import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useHomeWidget } from '../../../hooks/widget/home/useHomeWidget'

import Icon from '../../../components/ui/Icon'
import { Buttons } from '../../../components/buttons/Buttons'
import { HeroStatList } from '../../../components/guest/home/statList/HeroStatList'
import { HomeHeroCourt } from '../../../components/guest/home/heroCourt/HomeHeroCourt'

import { useApp } from '../../../contexts/AppContext'
import { useConfig } from '../../../contexts/ConfigContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { displayHourMinute } from '../../../utils/dateUtils'

const padCount = (count: number) => String(count).padStart(2, '0')

const toHourMinute = (time: string) =>
  displayHourMinute(time.length === 5 ? `${time}:00` : time)

export const Home = () => {
  const { t } = useTranslation('home')
  const { showBanner } = useApp()
  const { config } = useConfig()
  const { data: overview, error, resetState } = useHomeWidget()

  useEffect(() => {
    if (!error) {
      return
    }

    showBanner({ kind: BannerKind.ERR, msg: error })
    resetState?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const clubsCount = overview?.clubsCount ?? 0
  const courtsCount = overview?.courtsCount ?? 0
  const activeMembers = overview?.activeMembers
  const bookingWindow = config?.bookingWindowDays
  const hours =
    overview?.earliestOpenTime && overview?.latestCloseTime
      ? `${toHourMinute(overview.earliestOpenTime)}–${toHourMinute(overview.latestCloseTime)}`
      : '—'

  return (
    <>
      <div className="container-fluid g-0 hero p-xl-5">
        <div className="row hero-row flex-xl-row g-4 g-xl-0 gx-xl-5">
          <div className="col-12 col-xl-6 hero-content text-xl-start align-items-xl-start">
            <div className="eyebrow mb-16">
              {t('hero.eyebrow', {
                clubsCount: padCount(clubsCount),
                courtsCount: padCount(courtsCount),
                clubsLabel:
                  clubsCount === 1
                    ? t('hero.eyebrow.club')
                    : t('hero.eyebrow.clubs'),
                courtsLabel:
                  courtsCount === 1
                    ? t('hero.eyebrow.court')
                    : t('hero.eyebrow.courts'),
                activeMembersText:
                  activeMembers != null
                    ? ` · ${padCount(activeMembers)} ${
                        activeMembers === 1
                          ? t('hero.eyebrow.active-member')
                          : t('hero.eyebrow.active-members')
                      }`
                    : '',
              })}
            </div>
            <h1 className="hero-h">
              {t('hero.title.book')} <br />
              <span className="hero-title-highlight">
                {t('hero.title.court')}
              </span>
              .
              <br />
              {t('hero.title.find')} <br />
              <span className="hero-title-highlight">
                {t('hero.title.partner')}
              </span>
              .<br />
              {t('hero.title.play')}
            </h1>
            {bookingWindow != null && (
              <p className="hero-description mx-xl-0">
                {t('hero.description', { bookingWindow })}
              </p>
            )}
            <Buttons className="hero-actions flex-xl-row">
              <Link
                to="/guest-booking"
                className="btn rounded-2 btn-primary btn-lg btn-block w-100 w-xl-auto"
              >
                {t('hero.cta.start')}
                <Icon name="arrowR" size={16} />
              </Link>
              <Link
                to="/login"
                className="btn rounded-2 btn-ghost btn-lg btn-block w-100 w-xl-auto"
              >
                {t('hero.cta.account')}
              </Link>
            </Buttons>

            <HeroStatList
              hours={hours}
              sessionDuration={overview?.minSessionDuration}
              bookingWindow={bookingWindow}
            />
          </div>

          <div className="col-12 col-xl-6 hero-court-col align-self-xl-stretch">
            <HomeHeroCourt
              minOneEntryPrice={overview?.minSessionPrice ?? undefined}
              minOneEntrySlotDuration={overview?.minSessionPriceDuration ?? undefined}
              isOpen={overview?.isOpen}
            />
          </div>
        </div>
      </div>
    </>
  )
}
